import { Container, Graphics, Sprite } from "../pixi-runtime.js";
import { CatMotion } from "./cat-motion.js";
import { EYES, IRIS, FACE_PATHS, tracePaths } from "./cat-face-art.js";

function graphic(paths) {
  const result = new Graphics();
  for (const path of paths) {
    tracePaths(result, path.commands);
    if (path.fill) result.fill(path.fill);
    if (path.stroke)
      result.stroke({
        color: path.stroke,
        width: path.width,
        cap: "round",
        join: "round",
      });
  }
  return result;
}

/** A cutout face rig: textured head, independent eyes/pupils/lids/mouth/tears. */
export class CatRig {
  constructor(texture, seed = 0) {
    this.container = new Container();
    this.container.visible = false;
    this.motion = new CatMotion(seed);
    this.size = 87;
    const head = new Sprite(texture);
    head.anchor.set(0.5);
    head.width = 1000;
    head.height = 1000;
    this.container.addChild(head);
    this.eyes = EYES.map((eye) => {
      const root = new Container();
      root.position.set(eye.x, eye.y);
      root.rotation = eye.tilt;
      const open = new Container();
      const white = new Graphics().ellipse(0, 0, 115, 108).fill("#fffdf9");
      const pupil = new Graphics();
      for (const part of IRIS)
        pupil.ellipse(part.x, part.y, part.rx, part.ry).fill(part.color);
      open.addChild(white, pupil);
      const lid = graphic(FACE_PATHS.eyelid);
      lid.scale.x = eye.mirror;
      const closed = graphic(FACE_PATHS.closed);
      root.addChild(open, lid, closed);
      this.container.addChild(root);
      return { open, pupil, lid, closed };
    });
    this.neutral = graphic(FACE_PATHS.neutral);
    this.happy = graphic(FACE_PATHS.happy);
    this.sad = graphic(FACE_PATHS.sad);
    this.container.addChild(this.neutral, this.happy, this.sad);
    this.tears = [-1, 1].map((side) => {
      const tear = graphic(FACE_PATHS.tear);
      tear.x = side * 260;
      this.container.addChild(tear);
      return tear;
    });
  }
  setVisible(visible) {
    this.container.visible = visible;
  }
  reveal(time) {
    this.motion.reveal(time);
  }
  react(mood, time) {
    this.motion.react(mood, time);
  }
  update(time, reduced = false) {
    if (!this.container.visible) return;
    const pose = this.motion.sample(time, reduced),
      scale = (this.size / 1000) * pose.reveal;
    this.container.scale.set(scale * pose.xScale, scale * pose.yScale);
    this.container.rotation = pose.angle;
    this.container.y = (pose.bob * this.size) / 1000;
    for (const eye of this.eyes) {
      eye.open.scale.y = pose.eyeOpen;
      eye.pupil.position.set(pose.gazeX * 22, pose.gazeY * 12);
      eye.lid.alpha = pose.sad;
      eye.closed.alpha = Math.max(0, (0.16 - pose.eyeOpen) / 0.16);
    }
    this.neutral.alpha = 1 - Math.max(pose.happy, pose.sad);
    this.happy.alpha = pose.happy;
    this.sad.alpha = pose.sad;
    for (const tear of this.tears) {
      tear.y = 92 + pose.tear * 60;
      tear.alpha = pose.sad * 0.8;
    }
  }
}
